/**
 * Confidence Badge Component
 *
 * Displays confidence level for AI responses with visual styling per level:
 * - HIGH (emerald): Direct clause match or deterministic calculation
 * - MEDIUM (amber): Interpretation required, verification recommended
 * - LOW (orange): Insufficient or incomplete information
 * - REFUSE (rose): Cannot answer without missing inputs
 */

import { cn } from '@/lib/cn';
import { getConfidenceDisplay, type ConfidenceLevel } from '@/lib/confidence';
import { AlertTriangle, CheckCircle2, HelpCircle, XCircle } from 'lucide-react';

interface ConfidenceBadgeProps {
  level: ConfidenceLevel;
  percentage?: number;
  reason?: string;
  showPercentage?: boolean;
  size?: 'sm' | 'md';
  className?: string;
}

const icons = {
  high: CheckCircle2,
  medium: AlertTriangle,
  low: HelpCircle,
  refuse: XCircle,
};

export function ConfidenceBadge({
  level,
  percentage,
  reason,
  showPercentage = true,
  size = 'sm',
  className,
}: ConfidenceBadgeProps) {
  const display = getConfidenceDisplay(level);
  const Icon = icons[level];

  const sizeClasses = {
    sm: 'text-xs px-2 py-1 gap-1',
    md: 'text-sm px-3 py-1.5 gap-1.5',
  };

  const iconSize = size === 'sm' ? 'w-3 h-3' : 'w-4 h-4';

  return (
    <div className={cn('flex flex-col gap-1', className)}>
      {/* Main badge */}
      <div
        className={cn(
          'inline-flex items-center rounded-full font-medium',
          display.bgColor,
          display.borderColor,
          'border',
          sizeClasses[size]
        )}
      >
        <Icon className={cn(iconSize, display.color)} />
        <span className={display.color}>
          {showPercentage && percentage !== undefined && level !== 'refuse'
            ? `${percentage}% ${display.label}`
            : display.label}
        </span>
      </div>

      {/* Reason text for non-high confidence */}
      {reason && level !== 'high' && (
        <span
          className={cn(
            'text-xs',
            level === 'refuse' ? 'text-rose-400/80' : 'text-slate-500'
          )}
        >
          {reason}
        </span>
      )}
    </div>
  );
}

/**
 * Inline confidence indicator (smaller, for tight spaces)
 */
export function ConfidenceIndicator({
  level,
  className,
}: {
  level: ConfidenceLevel;
  className?: string;
}) {
  const display = getConfidenceDisplay(level);
  const Icon = icons[level];

  return (
    <div
      className={cn(
        'inline-flex items-center gap-1 text-xs',
        display.color,
        className
      )}
      title={`${display.label} confidence`}
    >
      <Icon className="w-3 h-3" />
      <span>{display.label}</span>
    </div>
  );
}

export default ConfidenceBadge;
